import React, {ChangeEvent, ReactNode, useEffect, useState} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {Badge} from '../Badge'
import {
  CaptionStyles,
  FieldStyles,
  FieldWrapperStyles,
  InputGroupStyles,
  InputLabel,
  InputTextStyles
} from '../InputText/styles'

interface Props {
  id: string
  name: string
  value?: string | number
  label?: string
  caption?: ReactNode
  isValid?: boolean
  isInvalid?: boolean
  isPrivate?: boolean
  onChange?: (value: string, event: ChangeEvent<HTMLTextAreaElement>) => void
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>
  autoComplete?: string
  className?: string
  dataId?: string
  textTransformType?: React.CSSProperties['textTransform']
  inputHeight?: number
  isResizable?: boolean
  disabled?: boolean
  required?: boolean
  readOnly?: boolean
}

const DEFAULT_INPUT_HEIGHT = 124

const Label = styled.label`
  ${FieldStyles}

  position: relative;
  padding: 0 0 0 ${cssTheme.layout.spacing.s400};
  align-items: flex-start;
  height: auto;
  gap: ${cssTheme.layout.spacing.s500};
  &:focus-within,
  &.__isFilled {
    .label {
      top: -9px;
      padding: 0 ${cssTheme.layout.spacing.s200};
      transform: translateX(-4px);
      &.__isInvalid {
        color: ${cssTheme.colors.input.caption.error};
      }
    }
  }
  &.__isResizable {
    textarea {
      resize: vertical;
      &:disabled {
        resize: none;
      }
    }
  }
  &.__isInvalid {
    color: ${cssTheme.colors.input.text.default};
    &:disabled {
      background: none;
      color: ${cssTheme.colors.input.caption.disabled};
    }
  }
`

const BadgeWithIcon = styled(Badge)`
  margin-top: ${cssTheme.layout.spacing.s400};
  position: absolute;
  right: ${cssTheme.layout.spacing.s400};
`

const Textarea = styled.textarea`
  border: none;
  background: none;
  outline: none;
  box-shadow: none;
  margin: 0;
  min-height: ${DEFAULT_INPUT_HEIGHT}px;
  border-radius: ${cssTheme.layout.radius.md};
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s800}
    ${cssTheme.layout.spacing.s300} 0 !important;
`

const CustomInputLabel = styled(InputLabel)`
  display: flex;
  align-items: flex-start;
  padding: ${cssTheme.layout.spacing.s300} 0;

  display: flex;
  align-items: flex-start;
  padding: ${cssTheme.layout.spacing.s300} 0;

  &.__isDisabled {
    color: ${cssTheme.colors.input.caption.disabled};
  }
`

export const InputTextarea = ({
  id,
  name,
  value,
  label,
  caption,
  isValid = false,
  isInvalid = false,
  isPrivate = false,
  onChange,
  onFocus,
  onBlur,
  autoComplete,
  className,
  dataId,
  textTransformType,
  inputHeight = DEFAULT_INPUT_HEIGHT,
  isResizable = true,
  disabled = false,
  required = false,
  readOnly = false
}: Props) => {
  const [isFilled, setIsFilled] = useState(Boolean(value))

  useEffect(() => {
    setIsFilled(Boolean(value))
  }, [value])

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setIsFilled(Boolean(event.target.value))
    onChange?.(event.target.value, event)
  }

  const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocus?.(event)
  }

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onBlur?.(event)
  }

  return (
    <div css={FieldWrapperStyles} className={className}>
      <Label
        htmlFor={id}
        className={cx(
          isInvalid && '__isInvalid',
          isFilled && '__isFilled',
          disabled && '__isDisabled',
          isResizable && '__isResizable',
          readOnly && '__isReadOnly'
        )}
      >
        <div css={InputGroupStyles}>
          <Textarea
            id={id}
            name={name}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            autoComplete={autoComplete}
            data-id={dataId}
            css={InputTextStyles}
            className={cx(isPrivate && 'fs-mask')}
            style={{
              textTransform: textTransformType,
              height: inputHeight
            }}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {label && (
            <CustomInputLabel
              className={cx(
                'label',
                disabled && '__isDisabled',
                isInvalid && '__isInvalid'
              )}
            >
              {label}
              {required && <span className="asterisk">*</span>}
            </CustomInputLabel>
          )}
        </div>
        {isValid && (
          <BadgeWithIcon size="xs" variant="success" iconName="Checkmark" />
        )}
        {isInvalid && (
          <BadgeWithIcon size="xs" variant="danger" iconName="WarningMark" />
        )}
      </Label>
      {caption && (
        <div
          css={CaptionStyles}
          className={cx(isInvalid && '__isInvalid', disabled && '__isDisabled')}
        >
          {caption}
        </div>
      )}
    </div>
  )
}
